import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Button,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ErrorMessage,
    MessageGuide
} from 'common/components';
import {
    RESTORE_MODAL_TITLE,
    RESTORE_MODAL_TEXT,
    RESTORE_INDICATOR_TO_CATEGORY_ERROR
} from 'common/config';
import { EntityType, Variant } from 'common/enums';
import { EntityVM } from 'common/models/data/entity.model';
import { RootState } from 'common/redux/core/root.reducer';

import SingleSelectMasterData from 'modules/master-data/components/single-select-master-data/SingleSelectMasterData';
import {
    getEntityHierarchyPath,
    getHierarchicalDataSelector
} from 'modules/archive/redux/archive.selector';

import './EntityRestore.scss';
import { HIERARCHY_PATH_DEFAULT_MESSAGE } from 'modules/archive/config';
import { getShowLoader } from 'common/redux/app-state/appState.selector';

type EntityRestoreProps = {
    isVisible: boolean;
    entityId: string;
    data: any;
    parentSubCategory?: EntityVM;
    variant: string;
    toggleExpansion: (payload: EntityVM) => void;
    updateSelectedRadioButton: (entity: EntityVM) => void;
    onSubmit: (data: any) => void;
    onClose: () => void;
    fetchNextHierachySet: () => void;
};

export default function EntityRestore({
    isVisible,
    data,
    parentSubCategory,
    variant,
    toggleExpansion,
    updateSelectedRadioButton,
    onSubmit,
    onClose,
    fetchNextHierachySet
}: EntityRestoreProps) {
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [parentEntity, setParentEntity] = useState<EntityVM>();

    const isLoaderVisible = useSelector((state: RootState) => getShowLoader(state));

    const hierarchicalData = useSelector((state: RootState) => getHierarchicalDataSelector(state));
    const hierarchicalPath = useSelector((state: RootState) => getEntityHierarchyPath(state));

    let restoreEntityPath = '';
    if (isLoaderVisible) {
        restoreEntityPath = '';
    } else if (hierarchicalPath !== null) {
        restoreEntityPath = 'Original Path: ' + hierarchicalPath;
    } else {
        restoreEntityPath = HIERARCHY_PATH_DEFAULT_MESSAGE;
    }

    const handleSubmit = () => {
        const updatedData = {
            ...data,
            targetId:
                parentEntity?.entityType === EntityType.Category
                    ? parentEntity?.id
                    : parentEntity?.hierarchyId,
            isImmediateChild: parentEntity?.entityType === EntityType.Category
        };
        onSubmit(updatedData);
    };

    const onRadioButtonSelect = (selectedData: EntityVM) => {
        setParentEntity(selectedData);
        updateSelectedRadioButton(selectedData);
        if (
            data.entityType === EntityType.Indicator &&
            selectedData.entityType === EntityType.Category
        ) {
            setErrorMessage(RESTORE_INDICATOR_TO_CATEGORY_ERROR);
        } else {
            setErrorMessage('');
        }
    };

    return (
        <Modal show={isVisible} onClose={onClose} className="entity-restore-modal">
            <ModalHeader className="entity-restore-header">{`${RESTORE_MODAL_TITLE}${
                variant ? variant : Variant.MasterData
            }!`}</ModalHeader>
            <ModalBody className="entity-restore-body">
                <div className="entity-restore-container">
                    <Label className="entity-restore-text" text={RESTORE_MODAL_TEXT} />
                    <SingleSelectMasterData
                        hideSelectionOnCategory={data.entityType === EntityType.Indicator}
                        hierarchicalData={hierarchicalData}
                        updateSelectedRadioButton={onRadioButtonSelect}
                        toggleExpansion={toggleExpansion}
                        hideChildIndicatorsOnly={true}
                        disbleEntity={parentSubCategory}
                        preventSelectionOfSubCategoriesHavingSubCategories={
                            data.entityType === EntityType.Indicator
                        }
                        preventSelectionOfSubCategoriesHavingIndicators={
                            data.entityType !== EntityType.Indicator
                        }
                        fetchNextHierachySet={fetchNextHierachySet}
                    />
                    {errorMessage && (
                        <ErrorMessage className="entity-restore-error" message={errorMessage} />
                    )}
                </div>
                <div className="entity-restore-path">{restoreEntityPath}</div>
            </ModalBody>
            <ModalFooter className="entity-restore-footer">
                <Button
                    size="medium"
                    type="button"
                    disabled={!!errorMessage || !parentEntity?.id}
                    onClick={handleSubmit}>
                    Submit
                </Button>
                <Button size="medium" variant="secondary" type="button" onClick={onClose}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
}
